import { Link } from 'react-router-dom';
import { useState } from 'react';
import { MapPin, Phone, Mail, Linkedin, Twitter, Facebook, Instagram, ArrowRight, Send, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickLinks = [
  { name: 'About Us', path: '/about' },
  { name: 'Why Warehaus+', path: '/why-warehaus' },
  { name: 'Products', path: '/products' },
  { name: 'Services', path: '/services' },
  { name: 'Industries', path: '/industries' },
  { name: 'Contact Us', path: '/contact' },
];

const products = [
  { name: 'Pallet Racking', path: '/products/standard-pallet-racking' },
  { name: 'ASRS Systems', path: '/products/asrs-racking' },
  { name: 'Mezzanine Floors', path: '/products/mezzanine-floors' },
  { name: 'Shuttle Racking', path: '/products/shuttle-racking' },
];

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This email is already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0a1628] relative overflow-hidden" data-testid="footer">
      {/* Top Gradient Border */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      {/* Background Decoration */}
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#E63946] opacity-[0.02] blur-[200px] rounded-full" />
      
      <div className="container-custom py-20 md:py-28 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-8 group">
              <div className="bg-white rounded px-2 py-1 inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/vjaqzlc7_IMG_0255.jpeg" 
                  alt="Warehaus+ - Designed to Store. Built to Scale."
                  className="h-10 w-auto object-contain group-hover:opacity-90 transition-opacity"
                />
              </div>
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-sm">
              India's scalable, rental-first warehouse infrastructure partner. 
              Transforming empty shells into future-ready facilities through our 
              Warehouse-as-a-Service model.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {[
                { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
                { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
                { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
                { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 hover:border-[#E63946]/50 hover:bg-[#E63946]/10 transition-all duration-300 group"
                  data-testid={`social-${social.label.toLowerCase()}`}
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4 text-gray-500 group-hover:text-[#E63946] transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="font-heading text-sm font-bold text-white uppercase tracking-[0.2em] mb-8">
              Quick Links
            </h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-500 hover:text-white text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E63946]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="lg:col-span-2">
            <h4 className="font-heading text-sm font-bold text-white uppercase tracking-[0.2em] mb-8">
              Products
            </h4>
            <ul className="space-y-4">
              {products.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-500 hover:text-white text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#E63946]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-4">
            <h4 className="font-heading text-sm font-bold text-white uppercase tracking-[0.2em] mb-8">
              Get in Touch
            </h4>
            
            <div className="space-y-5 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E63946]/10 flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#E63946]" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium mb-1">Corporate Office</p>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Gravique Pvt. Ltd.<br />
                    431, 4th Floor, Century Tower,<br />
                    H.B.Samaja Road, Bangalore 560004
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E63946]/10 flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#E63946]" />
                </div>
                <a href="mailto:info@warehausplus.com" className="text-gray-400 text-sm hover:text-white transition-colors">
                  info@warehausplus.com
                </a>
              </div>
            </div>

            {/* Newsletter */}
            <div className="p-6 bg-white/5 border border-white/5">
              <h5 className="text-white text-sm font-semibold mb-3">Subscribe to Newsletter</h5>
              <p className="text-gray-600 text-xs mb-4">Get industry insights and updates delivered to your inbox.</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2" data-testid="newsletter-form">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-[#0c1e35]/50 border-white/10 focus:border-[#E63946] rounded-none h-11 text-sm text-white placeholder:text-gray-600"
                  data-testid="newsletter-email"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary clip-slant h-11 px-4"
                  data-testid="newsletter-submit"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-16 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Warehaus+ | A Brand of Gravique Pvt Ltd. All rights reserved.
            </p>
            <div className="flex items-center gap-8">
              <Link to="/privacy" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
